import { selectEntityById } from "panels/content/side-panel/slice"
import store from "integrations/redux/store"
import { SidePanelLayerType } from "@nova/constants"
import { LiveVideoFlight } from "@nova/protobuf"
import { isAgoraImported, safeImportAgora } from "../LiveVideo/agoraHelper"
import { AbstractVideoController } from "./AbstractVideoController"
import { LiveVideoFlightLiveController } from "./LiveVideoFlightLiveController"
import { LiveVideoFlightRecordedController } from "./LiveVideoFlightRecordedController"
import { TypedEventEmitter } from "@nova/typed-event-emitter"
import { VideoFlightController } from "./VideoFlightController"
import { GuardClass } from "./hooks/useController"
import { LiveVideoFlightAnonymousController } from "./LiveVideoFlightAnonymousController"

type VideoControllerFactoryEvents = "controllerAvailabilityChanged" | "flushAllControllers"

type ControllerCreationOpts = {
    fetchDataImmediately?: true
    anonymous?: true
}
export type GetOrCreateVideoControllerParameters = {
    flightId: string
    options?: ControllerCreationOpts
}

export class VideoControllerFactory {
    static emitter = new TypedEventEmitter<VideoControllerFactoryEvents>().setMaxListeners(15)
    private static flightIdsToCreateControllersFor = new Set<string>()
    private static controllersByFlightId: Record<string, AbstractVideoController> = {}
    private static initializationPromises: Record<
        string,
        { promise: Promise<void>; abort: () => void } | undefined
    > = {}
    private constructor() {} // private constructor prevents `new VideoControllerFactory()`
    private static async createAndInitializeControllerForId(
        flightId: string,
        signal: AbortSignal,
        options?: ControllerCreationOpts
    ) {
        let controller: AbstractVideoController | undefined = undefined
        if (options?.anonymous) {
            controller = new LiveVideoFlightAnonymousController({ flightId })
        } else {
            const flight = selectEntityById(flightId)(store.getState())
            switch (flight?.type) {
                case SidePanelLayerType.LiveVideoFlight:
                    controller = (flight as LiveVideoFlight).live
                        ? new LiveVideoFlightLiveController({ flightId })
                        : new LiveVideoFlightRecordedController({ flightId })
                    break
                case SidePanelLayerType.VideoFlight:
                    controller = new VideoFlightController({ flightId })
                    break
            }
        }
        if (!controller) {
            throw new Error("Tried to create a VideoController with an invalid layer type")
        }
        await controller.initialize(signal)
        this.controllersByFlightId[flightId] = controller
        this.emitter.emit("controllerAvailabilityChanged")
    }
    private static async createController({
        flightId,
        options,
    }: GetOrCreateVideoControllerParameters) {
        if (this.controllersByFlightId[flightId]) {
            return
        }
        if (this.initializationPromises[flightId]) {
            await this.initializationPromises[flightId].promise
            return
        }
        const abortController = new AbortController()
        const initializationPromise = (async () => {
            try {
                await this.createAndInitializeControllerForId(
                    flightId,
                    abortController.signal,
                    options
                )
            } finally {
                delete this.initializationPromises[flightId]
            }
        })()
        this.initializationPromises[flightId] = {
            promise: initializationPromise,
            abort: () => abortController.abort(),
        }
        await initializationPromise
    }
    static async getOrCreateControllerForFlight({
        flightId,
        options,
    }: GetOrCreateVideoControllerParameters) {
        if (!flightId) {
            console.error("Tried to getOrCreateControllerForFlight missing a flightId")
            return
        }
        if (!isAgoraImported()) {
            const AgoraRTC = safeImportAgora()
            if (!AgoraRTC) {
                this.flightIdsToCreateControllersFor.add(flightId)
                return
            }
            AgoraRTC.setLogLevel(3)
            const backlogPromises = Array.from(this.flightIdsToCreateControllersFor).map((id) => {
                return this.createController({ flightId: id })
            })
            await Promise.all(backlogPromises)
            this.flightIdsToCreateControllersFor.clear()
        }
        const initializationPromise = this.createController({ flightId, options })
        await initializationPromise
        const controller = this.controllersByFlightId[flightId]
        if (options?.fetchDataImmediately && !controller.hasFetchedOriginalData()) {
            await controller.fetchAllData()
        }
        return controller
    }
    static async getOrCreateGuardedControllerForFlight<T>({
        flightId,
        options,
        guardClass,
    }: GetOrCreateVideoControllerParameters & { guardClass: GuardClass<T> }) {
        const controller = await this.getOrCreateControllerForFlight({ flightId, options })
        if (controller && controller instanceof guardClass) {
            return controller as T
        }
        return undefined
    }
    static async tearDownControllerForFlight({ flightId }: { flightId: string }) {
        const initializationPromise = this.initializationPromises[flightId]
        const controller = this.controllersByFlightId[flightId]
        delete this.initializationPromises[flightId]
        delete this.controllersByFlightId[flightId]
        this.emitter.emit("controllerAvailabilityChanged")
        if (initializationPromise) {
            initializationPromise.abort()
        }
        if (controller) {
            void controller.deinitialize()
        }
    }
    static async unsafeFlush() {
        /*
         * This method cleans up and removes all active LiveVideoFlightLiveControllers.
         * It should only be called when you are sure they are no longer needed.
         * ie. When switching projects.
         */
        for (const promise of Object.values(this.initializationPromises)) {
            promise?.abort()
        }
        for (const controller of Object.values(this.controllersByFlightId)) {
            await controller.deinitialize()
        }
        this.controllersByFlightId = {}
        this.initializationPromises = {}
        this.flightIdsToCreateControllersFor.clear()
        this.emitter.emit("flushAllControllers")
    }
}
